import { ChevronDown, ChevronRight, Database, Loader2 } from 'lucide-react'
import { useState } from 'react'
import type { CollectionInfo, DatabaseInfo } from '../types/mongoConsole.types'

interface Props {
  databases: DatabaseInfo[]
  isLoading: boolean
  selectedCollection: CollectionInfo | null
  onSelect: (col: CollectionInfo) => void
}

export function CollectionBrowser({ databases, isLoading, selectedCollection, onSelect }: Props) {
  const [expandedDbs, setExpandedDbs] = useState<Set<string>>(new Set(databases.map(d => d.name)))

  const toggleDb = (name: string) => {
    const next = new Set(expandedDbs)
    next.has(name) ? next.delete(name) : next.add(name)
    setExpandedDbs(next)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-1">
      {databases.map(db => (
        <div key={db.name}>
          <button
            onClick={() => toggleDb(db.name)}
            className="flex items-center gap-2 w-full px-2 py-1.5 rounded hover:bg-gray-100 text-sm font-semibold text-gray-700"
          >
            {expandedDbs.has(db.name)
              ? <ChevronDown className="h-3.5 w-3.5 text-gray-400 shrink-0" />
              : <ChevronRight className="h-3.5 w-3.5 text-gray-400 shrink-0" />}
            <Database className="h-3.5 w-3.5 text-blue-500 shrink-0" />
            <span className="truncate">{db.name}</span>
          </button>

          {expandedDbs.has(db.name) && (
            <div className="ml-4 mt-0.5 space-y-0.5">
              {db.collections.map(col => {
                const isSelected = selectedCollection?.name === col.name &&
                  selectedCollection?.database === col.database
                return (
                  <button
                    key={col.name}
                    onClick={() => onSelect(col)}
                    className={`flex items-center justify-between w-full px-2 py-1 rounded text-xs transition-colors ${
                      isSelected
                        ? 'bg-primary text-primary-foreground'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span className="truncate font-mono">{col.name}</span>
                    <span className={`shrink-0 ml-1 ${isSelected ? 'text-primary-foreground/70' : 'text-gray-400'}`}>
                      {col.documentCount.toLocaleString()}
                    </span>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
